// The dice a caster's sheet adds by itself (issue 1172; docs/MRCS/shadowrun-3.md §15): each line the system's
// `magic_dice` gives the casting or conjuring, as its own row ("Bear: +2 dice (health spells)"), with a switch. A line
// whose entry names a condition ("At night") starts off, for the person to judge; the DM can turn any line off for one
// test (a situation the book says cancels it). The server checks the lines' shape and bounds.
import { useEffect, useState } from 'react';
import { diceTermLine, type DiceTerm, type MagicLine } from '@dragons-table/vtt-shared';

/** The lines' switches, keyed by their words; reset when the lines change. */
export function useMagicLines(lines: MagicLine[]): { on: (i: number) => boolean; set: (i: number, v: boolean) => void; dice: DiceTerm[]; tn: Array<{ label: string; value: number }> } {
  const key = lines.map((l) => `${diceTermLine(l.term)}|${l.ask ?? ''}`).join(';');
  const [off, setOff] = useState<Record<number, boolean>>({});
  useEffect(() => { const o: Record<number, boolean> = {}; lines.forEach((l, i) => { if (l.ask) o[i] = true; }); setOff(o); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [key]);
  const on = (i: number) => !off[i];
  return {
    on, set: (i, v) => setOff((o) => ({ ...o, [i]: !v })),
    dice: lines.filter((l, i) => on(i) && !l.tn).map((l) => ({ ...l.term })),
    tn: lines.filter((l, i) => on(i) && l.tn).map((l) => ({ label: `${l.term.label}${l.term.note ? ` (${l.term.note})` : ''}`.slice(0, 40), value: l.term.value })),
  };
}

export function MagicDiceRows({ lines, state, isDm, testid }: { lines: MagicLine[]; state: ReturnType<typeof useMagicLines>; isDm: boolean; testid: string }) {
  if (!lines.length) return null;
  return (
    <div className="flex flex-col gap-0.5" data-testid={testid}>
      {lines.map((l, i) => (
        <label key={i} className="flex items-center gap-1 text-violet-200" title={l.ask ? `Only ${l.ask.toLowerCase()}: tick it when that holds.` : 'From the sheet. The DM can turn it off for this test.'}>
          <input type="checkbox" aria-label={`Count ${diceTermLine(l.term)}`} checked={state.on(i)} disabled={!isDm && !l.ask} onChange={(e) => state.set(i, e.target.checked)} />
          {l.tn ? `${l.term.label}: TN ${l.term.value > 0 ? '+' : '−'}${Math.abs(l.term.value)}${l.term.note ? ` (${l.term.note})` : ''}` : diceTermLine(l.term)}
        </label>
      ))}
    </div>
  );
}
